import { useMemo, useState } from 'react';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { npsColor, type NpsMapPoint } from '../../api/nps';

// Same us-atlas topojson the rankings map uses (served from /public).
const GEO_URL = '/us-states-10m.json';

/**
 * Map palette. The first version drew near-white land (#f1f5f9) with pale
 * borders (#cbd5e1) on a white panel — 1.10:1 land-to-panel and 1.36:1
 * border-to-land, which reads as a blank rectangle once it is on a projector
 * or pasted into a slide. These values put the country at 1.43:1 against the
 * panel and the state lines at 3.32:1 against the land, so the shape and the
 * borders survive a beamer, while staying light enough that the score dots —
 * particularly amber and orange — still carry.
 */
const LAND = '#cdd9e6';
const LAND_HOVER = '#b8c8da';
const BORDER = '#64748b';
/** Locations without enough responses to score. Was #cbd5e1: invisible on the
 *  land at 1.36:1, now 5.29:1 — plainly a dot, plainly not a score. */
const NO_SCORE = '#475569';

/** Deterministic sub-pixel jitter so co-located AFs don't collapse to one dot. */
function jitter(p: NpsMapPoint): [number, number] {
  const hash = [...p.afNumber].reduce((a, c) => a + c.charCodeAt(0), 0);
  const dx = ((hash % 17) - 8) / 17;
  const dy = (((hash * 31) % 17) - 8) / 17;
  return [p.lng + dx * 0.35, p.lat + dy * 0.35];
}

export default function NpsMap({
  points,
  onDrillIn,
}: {
  points: NpsMapPoint[];
  onDrillIn: (af: string) => void;
}) {
  const [hover, setHover] = useState<{ p: NpsMapPoint; x: number; y: number } | null>(null);
  const shown = useMemo(() => points.filter((p) => p.responses > 0), [points]);

  if (shown.length === 0) {
    return <div className="nps-respond-muted">No responses have been collected yet.</div>;
  }

  return (
    <div className="ds-panel" style={{ padding: 16, position: 'relative' }}
         onMouseLeave={() => setHover(null)}>
      <ComposableMap
        projection="geoAlbersUsa"
        projectionConfig={{ scale: 1000 }}
        width={975}
        height={610}
        style={{ width: '100%', height: 'auto', display: 'block' }}
      >
        <Geographies geography={GEO_URL}>
          {({ geographies }) =>
            geographies.map((geo) => (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                fill={LAND}
                stroke={BORDER}
                strokeWidth={0.8}
                style={{
                  default: { outline: 'none' },
                  hover: { fill: LAND_HOVER, outline: 'none' },
                  pressed: { outline: 'none' },
                }}
              />
            ))
          }
        </Geographies>
        {shown.map((p) => (
          <Marker
            key={p.afNumber}
            coordinates={jitter(p)}
            onClick={() => onDrillIn(p.afNumber)}
            onMouseEnter={(e) => {
              const rect = (e.currentTarget.ownerSVGElement!.parentElement as HTMLElement).getBoundingClientRect();
              setHover({ p, x: e.clientX - rect.left, y: e.clientY - rect.top });
            }}
            onMouseMove={(e) => {
              const rect = (e.currentTarget.ownerSVGElement!.parentElement as HTMLElement).getBoundingClientRect();
              setHover((h) => (h ? { ...h, x: e.clientX - rect.left, y: e.clientY - rect.top } : h));
            }}
            style={{ default: { cursor: 'pointer' } }}
          >
            <circle
              r={p.belowThreshold ? 3.5 : 5}
              fill={p.belowThreshold ? NO_SCORE : npsColor(p.nps)}
              fillOpacity={1}
              stroke="#fff"
              strokeWidth={1.3}
            />
          </Marker>
        ))}
      </ComposableMap>

      {hover && (
        <div
          style={{
            position: 'absolute',
            left: Math.min(hover.x + 12, 620),
            top: Math.max(hover.y - 8, 0),
            pointerEvents: 'none',
            background: '#0f172a',
            color: '#fff',
            padding: '8px 10px',
            borderRadius: 6,
            fontSize: 12,
            lineHeight: 1.4,
            boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
            whiteSpace: 'nowrap',
            zIndex: 10,
          }}
        >
          <div style={{ fontWeight: 600 }}>{hover.p.name}</div>
          <div style={{ opacity: 0.8 }}>
            {hover.p.afNumber}
            {hover.p.location ? ` · ${hover.p.location}` : ''}
          </div>
          <div style={{ marginTop: 4 }}>
            {hover.p.belowThreshold ? (
              <>{hover.p.responses} responses · not enough to score</>
            ) : (
              <>NPS <strong>{hover.p.nps! > 0 ? `+${hover.p.nps}` : hover.p.nps}</strong> · {hover.p.responses} responses</>
            )}
          </div>
        </div>
      )}

      <div className="nps-map-legend">
        <LegendDot color={npsColor(70)} label="70+" />
        <LegendDot color={npsColor(50)} label="50–69" />
        <LegendDot color={npsColor(30)} label="30–49" />
        <LegendDot color={npsColor(0)} label="0–29" />
        <LegendDot color={npsColor(-40)} label="Below 0" />
        <LegendDot color={NO_SCORE} label={`< min responses`} />
      </div>
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{
        display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
        background: color, border: '1px solid #fff', boxShadow: '0 0 0 1px #94a3b8',
      }} />
      {label}
    </span>
  );
}
